import { useQuery } from '@tanstack/react-query'
import { getProductImageHealth, type ProductImagePhase } from '@/api/health'
import { Panel } from './Panel'
import { StatusPill } from './StatusPill'

const phases: { key: ProductImagePhase; label: string }[] = [
  { key: 'sync', label: 'Listings' }, { key: 'harvest', label: 'Ernte' },
  { key: 'ingest', label: 'Laden' }, { key: 'dedup', label: 'Dubletten' }, { key: 'meta', label: 'Metadaten' },
]
const date = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
})
const berlinDate = (at: string) => `${date.format(new Date(at))} Uhr`
function duration(ms: number) {
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds} Sek.`
  if (seconds < 3600) return `${Math.floor(seconds / 60)} Min. ${seconds % 60} Sek.`
  return `${Math.floor(seconds / 3600)} Std. ${Math.floor(seconds % 3600 / 60)} Min.`
}
const states = {
  running: 'Nachtlauf läuft.', paused: 'Nachtlauf wird nächste Nacht fortgesetzt.',
  done: 'Letzter Nachtlauf abgeschlossen.', partial: 'Offene Fehler werden nächste Nacht erneut versucht.',
  skipped: 'Diese Nacht wurde ausgelassen.',
}
function coverageLabel(message: string) {
  if (message.startsWith('Metro')) return 'Metro stellt in dieser Quelle keine Bilder bereit.'
  if (message.startsWith('Shopify')) {
    if (message.includes('Varianten-Paginierungsgrenze')) return 'Shopify: Bei einigen Produkten konnten nicht alle Varianten gelesen werden.'
    if (message.includes('Media-Paginierungsgrenze')) return 'Shopify: Bei einigen Produkten konnten nicht alle Bilder gelesen werden.'
    if (message.includes('Produkt-Paginierungsgrenze')) return 'Shopify: Es konnten nicht alle Produkte gelesen werden.'
  }
  return message
}
const pill = { ok: { kind: 'ok', label: 'OK' }, degraded: { kind: 'low', label: 'Prüfen' }, aus: { kind: 'neutral', label: 'Aus' } } as const

/** Eigene Abfrage ohne Polling/Suspense: ein Fehler betrifft ausschließlich dieses Panel. */
export function ProductImageHealth() {
  const { data, isPending, isError } = useQuery({ queryKey: ['health', 'produktbilder'], queryFn: getProductImageHealth,
    staleTime: 60_000, retry: false, refetchOnWindowFocus: false })
  const state = data?.nightly
  const current = state?.phase === 'done' ? 'meta' : state?.phase
  return (
    <Panel title="Produktbilder-Nachtlauf" subtitle="täglich 04:40–06:40">
      {isError ? <p role="status" className="text-sm text-ink-2">Status konnte nicht geladen werden – Seite neu laden</p>
        : isPending || !data || !state ? <p role="status" className="text-sm text-ink-2">Status wird geladen…</p>
        : <div className="space-y-4 min-w-0">
          <ol aria-label="Arbeitsschritte" className="grid grid-cols-5 gap-1.5 sm:gap-3">
            {phases.map(({ key, label }) => {
              const completed = state.completed.includes(key)
              const active = current === key
              return <li key={key} aria-current={active ? 'step' : undefined} className="min-w-0">
                <div aria-hidden="true" className={`h-1.5 rounded-sm border ${completed ? 'bg-primary border-primary' : active ? 'bg-primary-soft border-primary' : 'bg-panel-2 border-line'}`} />
                <span data-phase-label className={`block mt-1.5 text-[10px] sm:text-xs break-words ${active ? 'text-ink font-semibold' : 'text-ink-2'}`}>{label}</span>
                {completed && <span className="sr-only">Erledigt</span>}
              </li>
            })}
          </ol>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill {...pill[data.status]} />
            <p className="text-xs text-ink-2">{!data.flags.nightly ? 'Nachtlauf ist ausgeschaltet.'
              : state.state ? states[state.state] : 'Noch kein Nachtlauf gelaufen'}</p>
          </div>
          <dl className="flex flex-wrap gap-x-8 gap-y-2 text-xs">
            <div><dt className="text-ink-2">Letzter Erfolg · Berlin</dt><dd className="text-ink mt-1">{state.lastSuccess ? berlinDate(state.lastSuccess) : 'Noch kein erfolgreicher Nachtlauf'}</dd></div>
            <div><dt className="text-ink-2">Dauer</dt><dd className="text-ink mt-1 tabular-nums">{state.phase ? duration(state.durationMs) : '–'}</dd></div>
          </dl>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 border-y border-line py-3">
            {([
              ['Neu gespeichert', state.counters.gespeichert], ['Schon vorhanden', state.counters.schon_da],
              ['Übersprungen', state.counters.uebersprungen], ['Fehler', state.counters.fehlerhaft],
            ] as const).map(([label, value]) => <div key={label} className="min-w-0">
              <dt className="text-[11px] text-ink-2">{label}</dt>
              <dd className="font-display text-lg text-ink tabular-nums mt-0.5">{value.toLocaleString('de-DE')}</dd>
            </div>)}
          </dl>
          <div className="text-xs text-ink-2 space-y-1">
            <p className="font-medium text-ink">{data.flags.backup ? 'Sicherung ist eingeschaltet.' : 'Sicherung ist ausgeschaltet.'}</p>
            <p>{data.backup.pendingFiles.toLocaleString('de-DE')} {data.backup.pendingFiles === 1 ? 'ungesicherte Datei' : 'ungesicherte Dateien'}</p>
            {data.backup.oldestPendingAt && <p>Älteste seit {berlinDate(data.backup.oldestPendingAt)}
              {data.backup.oldestPendingAgeHours !== null && ` (${Math.floor(data.backup.oldestPendingAgeHours)} Std.)`}</p>}
          </div>
          {state.coverage.length > 0 && <div className="text-xs text-ink-2 space-y-1">
            <p className="font-medium text-ink">Quellen mit Einschränkungen</p>
            {[...new Set(state.coverage.map(coverageLabel))].map(text => <p key={text} className="break-words">{text}</p>)}
          </div>}
          {state.openErrors > 0 && <details className="border-t border-line pt-3 text-xs">
            <summary tabIndex={0} className="cursor-pointer text-ink font-medium rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-panel">
              {state.openErrors.toLocaleString('de-DE')} {state.openErrors === 1 ? 'offener Fehler' : 'offene Fehler'}
            </summary>
            <ul className="mt-3 space-y-3 text-ink-2">
              {state.errors.slice(0, 20).map((error, i) => <li key={i} className="break-words [overflow-wrap:anywhere]">
                <span className="text-ink font-medium">{phases.find(p => p.key === error.phase)?.label ?? 'Bild'} · {error.key}</span>
                <p className="mt-0.5">{error.grund}</p>
              </li>)}
            </ul>
            {state.openErrors > 20 && <p className="mt-3 text-ink-2">Die ersten 20 Fehler werden angezeigt.</p>}
          </details>}
        </div>}
    </Panel>
  )
}
